import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  Text,
  Flex,
} from '@chakra-ui/react';
import { SharedDisclosureProps } from '@texas/components/shared/types';
import {
  suppliersApi,
  SupplierSearchQuery,
} from '@texas/api/endpoints/suppliersApi';
import { useApiResource } from '@texas/api/hooks/useApiResource';
import { DataTableContainer } from '@texas/components/shared/dataTable/DataTableContainer';
import { suppliersColumns } from '@texas/components/suppliers/Columns';
import { SearchFilterInput } from '@texas/components/SearchFilterInput';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ExportSuppliersButton } from '@texas/components/suppliers/ExportSuppliersButton';

interface SuppliersModalProps extends SharedDisclosureProps {
  supplierIds: number[];
  branchName: string;
  productGroupName: string;
  materialName?: string;
}

export function SuppliersModal({
  isOpen,
  onClose,
  supplierIds,
  branchName,
  productGroupName,
  materialName,
}: SuppliersModalProps) {
  const { t } = useTranslation();

  const title = materialName
    ? `${t(
        'supplierCoverageHeatmap.suppliersModal.title',
      ).toString()} - ${branchName} - ${productGroupName} - ${materialName}`
    : `${t(
        'supplierCoverageHeatmap.suppliersModal.title',
      ).toString()} - ${branchName} - ${productGroupName}`;

  return (
    <Modal size="6xl" isOpen={isOpen} onClose={onClose}>
      <ModalOverlay />
      <ModalContent maxW="1400px">
        <ModalHeader>
          <Flex justify="space-between" align="center">
            <Text fontSize="lg" fontWeight="bold">
              {title}
            </Text>
          </Flex>
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <InnerComponent supplierIds={supplierIds} />
        </ModalBody>
      </ModalContent>
    </Modal>
  );
}

interface InnerComponentProps {
  supplierIds: number[];
}

const defaultSearchQuery: SupplierSearchQuery = {
  sortDesc: false,
  searchTerm: '',
  includeArchived: false,
  page: 1,
  pageSize: 24,
  organizationId: 3,
  sortBy: '',
};

function InnerComponent({ supplierIds }: InnerComponentProps) {
  const { t } = useTranslation();

  const [searchQuery, setSearchQuery] = useState<SupplierSearchQuery>({
    ...defaultSearchQuery,
    supplierIds,
  });

  const {
    data: suppliers,
    refetch,
    loading,
    error,
  } = useApiResource(suppliersApi.searchSuppliers);

  useEffect(() => {
    if (supplierIds.length > 0) {
      refetch(searchQuery);
    }
  }, [refetch, searchQuery, supplierIds]);

  const columns = useMemo(() => {
    return suppliersColumns(t);
  }, [t]);

  return (
    <Flex direction="column" gap={4}>
      <Flex direction="row" justify="space-between" align="center">
        <Flex direction="column" gap={2} flex={1}>
          <SearchFilterInput
            value={searchQuery.searchTerm}
            onChange={(s) =>
              setSearchQuery({ ...searchQuery, searchTerm: s, page: 1 })
            }
          />
          <Text fontSize="sm" fontWeight="normal" color="gray.500">
            {t('supplierCoverageHeatmap.suppliersModal.subtitle', {
              count: suppliers?.items.totalItems ?? 0,
            })}
          </Text>
        </Flex>
        <ExportSuppliersButton
          supplierIds={suppliers?.supplierIds}
          organizationId={searchQuery.organizationId}
          isDisabled={loading}
          ml={4}
          variant="texas-outline-light"
        >
          {t('general.exportExcel')}
        </ExportSuppliersButton>
      </Flex>
      <DataTableContainer
        w="100%"
        minH={0}
        display="flex"
        flexDir="column"
        error={error}
        datatable={{
          data: suppliers?.items.items ?? [],
          columns: columns,
          isLoading: loading,
          sorted: {
            onSortedChange: ({ key, desc }) => {
              setSearchQuery({
                ...searchQuery,
                sortBy: key,
                sortDesc: desc,
              });
            },
            key: searchQuery.sortBy,
            desc: searchQuery.sortDesc,
          },
          variant: 'configuration',
        }}
        pagination={{
          totalItems: suppliers?.items.totalItems ?? 0,
          pageSize: searchQuery.pageSize,
          currentPage: searchQuery.page,
          onPageChange: (page) =>
            setSearchQuery({
              ...searchQuery,
              page,
            }),
        }}
      />
    </Flex>
  );
}
